import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { BulkImportCompleteTable } from "@/features/learner/components/learner-bulk-import/BulkImportCompleteTable";
import {
  buildAccountRowFromImport,
  TBulkAccountRow,
  TBulkImportRow,
} from "@/features/learner/components/learner-bulk-import/bulk-import-mock";

// CSV インジェクション対策 + クォート。=,+,-,@ や先頭タブ/改行で始まる値は ' を前置して
// Excel/Sheets で数式として実行されないようにし、内部の " は "" にエスケープする。
const escapeCsvCell = (value: string | null | undefined): string => {
  const v = value ?? "";
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
};

export const BulkImportCompletePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 発行画面から実際の発行結果(accountRows)を受け取る。
  // 無い場合(直接アクセス等)は編集行→モックのログイン情報で表示する。
  const state = location.state as { accountRows?: TBulkAccountRow[]; rows?: TBulkImportRow[] } | null;
  const accountRows = useMemo(
    () => state?.accountRows ?? (state?.rows ?? []).map(buildAccountRowFromImport),
    [state],
  );

  const goLearnerList = () => navigate(AppRoutes.learnerList);

  // 発行されたアカウント情報を CSV で一括ダウンロード (Excel向けに BOM 付き)
  const handleExportCsv = () => {
    const header = ["氏名", "所属機関", "受験分野", "国籍", "性別", "ログインURL", "学習者ID", "パスワード"];
    const body = accountRows.map((r) => [r.fullName, r.companyName, r.examFieldLabel, r.nationality, r.gender, r.loginUrl, r.learnerId, r.password]);
    const csv = [header, ...body].map((cols) => cols.map(escapeCsvCell).join(",")).join("\r\n");
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "アカウント情報.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack gap={2} height="calc(100vh - 105px)">
      <Stack gap={0.5} flexShrink={0}>
        <Typography variant="head20Bold">外国人の一括アカウント発行</Typography>
        <Typography variant="cap12Light" color={text.secondary}>
          アカウントを発行しました。ログイン情報を各学習者へ共有してください。
        </Typography>
      </Stack>

      {/* テーブルは内容の高さで表示 (最終行が枠に密着、下に余白を作らない)。件数が少ない時はカードも縮む */}
      <Stack flexShrink={0} gap={1.5} sx={{ border: `1px solid ${divider.high}`, borderRadius: rounded.sm, p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}>
          <Typography variant="body14Semi" color={text.primary}>
            アカウント発行件数： {accountRows.length} 件
          </Typography>
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={accountRows.length === 0}>
            <Typography variant="body14Semi">CSVでアカウント情報を一括出力</Typography>
          </Button>
        </Stack>

        <BulkImportCompleteTable rows={accountRows} />
      </Stack>

      {/* 余白を埋めてフッターを最下部に固定 */}
      <Box flex={1} minHeight={0} />

      <FooterContainer>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" height="100%">
          <Button variant="outline" size="md" onClick={goLearnerList}>
            <Typography variant="body14Semi">外国人一覧へ戻る</Typography>
          </Button>
        </Stack>
      </FooterContainer>
    </Stack>
  );
};
